import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Calendar,
  Clock,
  Users,
  Award,
  ArrowLeft,
  AlertCircle,
  CheckCircle,
  XCircle,
  Upload,
  Edit,
  Ban,
  UsersIcon,
  UserPlus,
  UserCheck,
  HandHeart,
  Camera
} from 'lucide-react';
import { eventsService, charactersService } from '../services';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import PageContainer from '../components/PageContainer';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import { LoadingSpinner } from '../components/ui/Loading';
import DeclineEventModal from '../components/modals/DeclineEventModal';
import SubmitProofModal from '../components/SubmitProofModal';
import EditEventModal from '../components/modals/EditEventModal';
import CancelEventModal from '../components/modals/CancelEventModal';
import PartyGeneratorModal from '../components/modals/PartyGeneratorModal';
import BulkConfirmParticipantsModal from '../components/modals/BulkConfirmParticipantsModal';
import MultiCharacterParticipationModal from '../components/modals/MultiCharacterParticipationModal';
import LendCharacterModal from '../components/modals/LendCharacterModal';
import NonParticipantsModal from '../components/modals/NonParticipantsModal';
import {getEventTypeTranslation} from "../utils/eventTypeTranslation";
import {getImageUrl} from "../utils/imageUtils";

const EventDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { notify } = useNotification();
  const [event, setEvent] = useState(null);
  const [characters, setCharacters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [proofModalParticipation, setProofModalParticipation] = useState(null); // specific participation for proof
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showPartyModal, setShowPartyModal] = useState(false);
  const [showBulkConfirmModal, setShowBulkConfirmModal] = useState(false);
  const [showMultiCharModal, setShowMultiCharModal] = useState(false);
  const [showLendCharModal, setShowLendCharModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [editingPilot, setEditingPilot] = useState(null);
  const [pilotValue, setPilotValue] = useState('');
  const [showNonParticipantsModal, setShowNonParticipantsModal] = useState(false);
  const [nonParticipants, setNonParticipants] = useState([]);
  const [loadingNonParticipants, setLoadingNonParticipants] = useState(false);
  const [selectedProof, setSelectedProof] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    loadEventDetails();
    loadCharacters();
  }, [id]);

  const loadCharacters = async () => {
    try {
      const response = await charactersService.getMeCharacters();
      setCharacters(response.characters || []);
    } catch (err) {
      console.error('Error loading characters:', err);
    }
  };

  const loadEventDetails = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await eventsService.getEvent(id);

      // Backend retorna diretamente sem wrapper 'event'
      const eventData = response.event || response;

      setEvent(eventData);
    } catch (err) {
      console.error('❌ [EventDetail] Error loading event:', err);
      setError(err.response?.data?.error || 'Erro ao carregar detalhes do evento');
    } finally {
      setLoading(false);
    }
  };

  const loadNonParticipants = async () => {
    try {
      setLoadingNonParticipants(true);
      const response = await eventsService.getNonParticipants(id);
      setNonParticipants(response.nonParticipants || []);
    } catch (err) {
      console.error('Error loading non-participants:', err);
      notify.error('Erro ao carregar não participantes', 'Erro');
    } finally {
      setLoadingNonParticipants(false);
    }
  };

  const handleParticipate = async (characterId) => {
    try {
      setActionLoading(true);
      await eventsService.participateInEvent(id, {
        characterId,
        status: 'CONFIRMED'
      });
      notify.success('Participação confirmada com sucesso!', 'Confirmado');
      await loadEventDetails();
    } catch (err) {
      console.error('Error participating:', err);
      notify.error(err.response?.data?.error || 'Erro ao confirmar participação', 'Erro');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeclineClick = () => {
    setShowDeclineModal(true);
  };

  const handleDeclineConfirm = async (reason) => {
    try {
      setActionLoading(true);
      const characterId = characters[0]?.id;
      if (!characterId) {
        notify.error('Você precisa ter um personagem para declinar um evento', 'Erro');
        return;
      }

      await eventsService.participateInEvent(id, {
        characterId,
        status: 'DECLINED',
        declineReason: reason || undefined
      });

      notify.success('Ausência informada com sucesso!', 'Confirmado');
      setShowDeclineModal(false);
      await loadEventDetails();
    } catch (err) {
      console.error('Error declining event:', err);
      notify.error(err.response?.data?.error || 'Erro ao informar ausência', 'Erro');
    } finally {
      setActionLoading(false);
    }
  };

  const handleEditEvent = async (eventData) => {
    try {
      setActionLoading(true);
      await eventsService.updateEvent(id, eventData);
      notify.success('Evento atualizado com sucesso!', 'Sucesso');
      setShowEditModal(false);
      await loadEventDetails();
    } catch (err) {
      console.error('Error updating event:', err);
      notify.error(err.response?.data?.error || 'Erro ao atualizar evento', 'Erro');
      throw err;
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelEvent = async (reason) => {
    try {
      await eventsService.cancelEvent(id, { cancellationReason: reason });
      notify.success('Evento cancelado com sucesso!', 'Cancelado');
      await loadEventDetails();
    } catch (err) {
      console.error('Error canceling event:', err);
      notify.error(err.response?.data?.error || 'Erro ao cancelar evento', 'Erro');
    }
  };

  const handleBulkConfirm = async (characterIds) => {
    try {
      setActionLoading(true);
      const response = await eventsService.bulkConfirmParticipants(id, characterIds);
      notify.success(response.message || 'Participantes confirmados com sucesso!', 'Sucesso');
      await loadEventDetails();
    } catch (err) {
      console.error('Error bulk confirming participants:', err);
      notify.error(err.response?.data?.error || 'Erro ao confirmar participantes', 'Erro');
    } finally {
      setActionLoading(false);
      setShowBulkConfirmModal(false);
    }
  };

  const handleEditPilot = (participation) => {
    setEditingPilot(participation.id);
    setPilotValue(participation.pilot || '');
  };

  const handleSavePilot = async (participationId) => {
    try {
      await eventsService.updateParticipationPilot(participationId, pilotValue);
      notify.success('Piloto atualizado com sucesso!', 'Sucesso');
      setEditingPilot(null);
      setPilotValue('');
      await loadEventDetails();
    } catch (err) {
      console.error('Error updating pilot:', err);
      notify.error(err.response?.data?.error || 'Erro ao atualizar piloto', 'Erro');
    }
  };

  const handleCancelEditPilot = () => {
    setEditingPilot(null);
    setPilotValue('');
  };

  const handleAdminConfirmParticipation = async (participationId) => {
    if (!window.confirm('Confirmar a participação deste personagem sem necessidade de prova?')) {
      return;
    }

    try {
      setActionLoading(true);
      await eventsService.adminConfirmParticipation(participationId);
      notify.success('Participação confirmada com sucesso!', 'Sucesso');
      await loadEventDetails();
    } catch (err) {
      console.error('Error confirming participation:', err);
      notify.error(err.response?.data?.error || 'Erro ao confirmar participação', 'Erro');
    } finally {
      setActionLoading(false);
    }
  };

  const openModal = (proof) => {
    setSelectedProof(proof);
    setShowModal(true);
  };

  const handleMultiCharParticipation = async (participations) => {
    try {
      setActionLoading(true);
      const response = await eventsService.participateMultipleCharacters(id, participations);
      notify.success(response.message || 'Participações confirmadas com sucesso!', 'Sucesso');
      setShowMultiCharModal(false);
      await loadEventDetails();
    } catch (err) {
      console.error('Error confirming participations:', err);
      notify.error(err.response?.data?.error || 'Erro ao confirmar participações', 'Erro');
    } finally {
      setActionLoading(false);
    }
  };

  const handleLendCharacter = async (lendData) => {
    try {
      setActionLoading(true);
      await eventsService.participateInEvent(id, {
        characterId: lendData.characterId,
        status: 'CHARACTER_LENT',
        pilot: lendData.pilot,
        lentHasPilot: lendData.lentHasPilot,
        lentLoginId: lendData.lentLoginId,
        lentTempPassword: lendData.lentTempPassword,
      });
      notify.success('Personagem emprestado com sucesso!', 'Empréstimo');
      setShowLendCharModal(false);
      await loadEventDetails();
    } catch (err) {
      console.error('Error lending character:', err);
      notify.error(err.response?.data?.error || 'Erro ao emprestar personagem', 'Erro');
    } finally {
      setActionLoading(false);
    }
  };

  const getEventTypeIcon = (type) => {
    const icons = {
      PVP: '⚔️',
      BC: '🏰',
      MEETING: '👥',
      OTHER: '🎯',
    };
    return icons[type] || '📅';
  };

  const getStatusColor = (status) => {
    const colors = {
      SCHEDULED: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      ACTIVE: 'bg-green-500/20 text-green-400 border-green-500/30',
      COMPLETED: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      CANCELLED: 'bg-red-500/20 text-red-400 border-red-500/30',
    };
    return colors[status] || 'bg-gray-500/20 text-gray-400 border-gray-500/30';
  };

  const getStatusText = (status) => {
    const texts = {
      SCHEDULED: 'Agendado',
      ACTIVE: 'Em Andamento',
      COMPLETED: 'Concluído',
      CANCELLED: 'Cancelado',
    };
    return texts[status] || status;
  };

   const getStatusParticipationText = (status) => {
    const texts = {
      CONFIRMED: 'Confirmado',
      DECLINED: 'Recusado',
      PENDING: 'Pendente',
      PROOF_SUBMITTED: 'Prova Enviada',
      PROOF_REJECTED: 'Prova Rejeitada',
      PROOF_APPROVED: 'Prova Aceita',
      POINTS_AWARDED: 'Pontos Creditados',
      AWAITING_EVIDENCE: 'Aguardando prova de participação',
      CHARACTER_LENT: 'Personagem Emprestado',
    };

    return texts[status] || status;
  };

  if (loading) {
    return (
      <PageContainer title="Detalhes do Evento" description="Carregando...">
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      </PageContainer>
    );
  }

  if (error || !event) {
    return (
      <PageContainer
        title="Erro"
        actions={
          <Button variant="outline" onClick={() => navigate('/app/events')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
        }
      >
        <Card variant="glass" className="p-8 text-center">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">
            {error || 'Evento não encontrado'}
          </h3>
          <p className="text-gray-400 mb-6">
            Não foi possível carregar os detalhes do evento.
          </p>
          <Button onClick={() => navigate('/app/events')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar para Eventos
          </Button>
        </Card>
      </PageContainer>
    );
  }

  const participants = event.participations || [];
  const confirmedParticipants = participants.filter(p => p.status !== 'DECLINED');
  const confirmedCount = confirmedParticipants.length;

  // Check user participation
  const userParticipation = participants.find(p => p.userId === user?.id);
  const canParticipate = event.status === 'SCHEDULED' && !userParticipation;

  // Check if user can edit/cancel (admin or creator)
  const canManageEvent = user?.role === 'ADMIN' || event.createdBy === user?.id;
  const canCancelEvent = canManageEvent && event.status !== 'COMPLETED' && event.status !== 'CANCELLED';
  
  // Check if can generate parties (PVP or BC events with confirmed participants)
  const canGenerateParties = canManageEvent && 
    (event.type === 'PVP' || event.type === 'BC') && 
    confirmedParticipants.length > 0;

  const canPilot = (event.type === 'PVP' || event.type === 'BC');

  return (
    <PageContainer
      title={event.name}
      description={`Evento ${getStatusText(event.status)}`}
      actions={
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-2">
          {user?.role === 'ADMIN' && (event.status === 'SCHEDULED' || event.status === 'COMPLETED') && (
            <Button
              variant="outline"
              onClick={() => setShowBulkConfirmModal(true)}
              className="border-green-500/30 text-green-400 hover:bg-green-500/10"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Confirmar Participantes
            </Button>
          )}
          {canGenerateParties && (
            <Button
              variant="outline"
              onClick={() => setShowPartyModal(true)}
              className="border-purple-500/30 text-purple-400 hover:bg-purple-500/10"
            >
              <UsersIcon className="w-4 h-4 mr-2" />
              Gerar Grupos
            </Button>
          )}
          {canManageEvent && event.status !== 'COMPLETED' && event.status !== 'CANCELLED' && (
            <Button
              variant="outline"
              onClick={() => setShowEditModal(true)}
              className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10"
            >
              <Edit className="w-4 h-4 mr-2" />
              Editar
            </Button>
          )}
          {canCancelEvent && (
            <Button
              variant="outline"
              onClick={() => setShowCancelModal(true)}
              className="border-red-500/30 text-red-400 hover:bg-red-500/10"
            >
              <Ban className="w-4 h-4 mr-2" />
              Cancelar
            </Button>
          )}
          {user?.role === 'ADMIN' && (
            <Button
              variant="outline"
              onClick={() => {
                setShowNonParticipantsModal(true);
                loadNonParticipants();
              }}
              className="border-purple-500/30 text-purple-400 hover:bg-purple-500/10"
            >
              <Users className="w-4 h-4 mr-2" />
              Contatar Não Participantes
            </Button>
          )}
          <Button variant="outline" onClick={() => navigate('/app/events')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
        </div>
      }
    >
      {/* Header Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="mb-8"
      >
        <Card variant="glass" className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 to-blue-600/10"></div>
          <div className="relative p-8">
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center text-3xl">
                  {getEventTypeIcon(event.type)}
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white mb-2">{event.name}</h2>
                  <div className={`inline-flex items-center px-3 py-1 rounded-lg text-sm font-medium border ${getStatusColor(event.status)}`}>
                    {getStatusText(event.status)}
                  </div>
                </div>
              </div>
              {event.points > 0 && (
                <div className="bg-gradient-to-br from-amber-500/20 to-orange-500/20 border border-amber-500/30 rounded-xl px-4 py-3 text-center">
                  <div className="text-2xl font-bold text-amber-400">{event.points}</div>
                  <div className="text-xs text-amber-500 font-medium">pontos</div>
                </div>
              )}
            </div>

            {event.description && (
              <p className="text-gray-300 mb-6">{event.description}</p>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="flex items-center gap-3 bg-white/5 rounded-xl p-4">
                <Calendar className="w-5 h-5 text-cyan-400" />
                <div>
                  <p className="text-xs text-gray-400">Data de Início</p>
                  <p className="text-sm font-semibold text-white">
                    {new Date(event.startDate).toLocaleDateString('pt-BR')}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3 bg-white/5 rounded-xl p-4">
                <Clock className="w-5 h-5 text-blue-400" />
                <div>
                  <p className="text-xs text-gray-400">Horário</p>
                  <p className="text-sm font-semibold text-white">
                    {new Date(event.startDate).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3 bg-white/5 rounded-xl p-4">
                <Users className="w-5 h-5 text-green-400" />
                <div>
                  <p className="text-xs text-gray-400">Confirmados</p>
                  <p className="text-sm font-semibold text-white">{confirmedCount}</p>
                </div>
              </div>

              <div className="flex items-center gap-3 bg-white/5 rounded-xl p-4">
                <Award className="w-5 h-5 text-purple-400" />
                <div>
                  <p className="text-xs text-gray-400">Tipo</p>
                  <p className="text-sm font-semibold text-white">{getEventTypeTranslation(event.type)}</p>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </motion.div>

      {/* Participation Actions */}
      {event.status === 'SCHEDULED' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          className="mb-8"
        >
          <Card variant="glass">
            <div className="p-6">
              <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <Users className="w-6 h-6 text-cyan-400" />
                Gerenciar Participação
              </h3>

              {/* Get user's participations */}
              {(() => {
                const userParticipations = participants.filter(p => p.userId === user?.id);
                const hasParticipations = userParticipations.length > 0;
                
                return (
                  <div className="space-y-4">
                    {hasParticipations && (
                      <div className="space-y-2">
                        <p className="text-sm text-gray-400 mb-3">
                          Seus personagens confirmados ({userParticipations.length}):
                        </p>
                        {userParticipations.map(participation => (
                          <div key={participation.id} className="bg-white/5 rounded-xl p-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg flex items-center justify-center text-white font-bold">
                                  {participation.character?.name?.[0] || '?'}
                                </div>
                                <div>
                                  <p className="font-semibold text-white">
                                    {participation.character?.name || 'Personagem'}
                                  </p>
                                  <p className="text-xs text-gray-400">
                                    {participation.character?.class} • Nv. {participation.character?.level}
                                    <br />
                                    Piloto: {participation.pilot || 'Não definido'}      
                                    <button
                                      onClick={() => handleEditPilot(participation)}
                                      className="text-cyan-400 hover:text-cyan-300 text-xs underline ml-1"
                                    >
                                      editar
                                    </button>
                                  </p>
                                </div>
                              </div>
                              <div className={`px-3 py-1 rounded-lg text-xs font-medium ${
                                participation.status === 'CONFIRMED'
                                  ? 'bg-green-500/20 text-green-400'
                                  : participation.status === 'CHARACTER_LENT'
                                  ? 'bg-amber-500/20 text-amber-400'
                                  : 'bg-gray-500/20 text-gray-400'
                              }`}>
                                {participation.status === 'CONFIRMED' ? '✓ Confirmado' :
                                 participation.status === 'CHARACTER_LENT' ? '🤝 Emprestado' :
                                 getStatusParticipationText(participation.status)}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    <div
                      className={canPilot ? 'grid grid-cols-1 md:grid-cols-3 gap-3' : 'grid grid-cols-1 md:grid-cols-2 gap-2'}>
                      <Button
                        onClick={() => setShowMultiCharModal(true)}
                        disabled={actionLoading}
                        className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700"
                        fullWidth
                      >
                        <UserCheck className="w-4 h-4 mr-2" />
                        {hasParticipations ? 'Gerenciar Personagens' : 'Confirmar Participação'}
                      </Button>

                      {canPilot && (
                        <Button
                          variant="outline"
                          onClick={() => setShowLendCharModal(true)}
                          disabled={actionLoading}
                          fullWidth
                          className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
                        >
                          <HandHeart className="w-4 h-4 mr-2"/>
                          Não posso ir, mas empresto personagem
                        </Button>
                      )}

                      <Button
                        variant="outline"
                        onClick={handleDeclineClick}
                        disabled={actionLoading}
                        fullWidth
                        className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        Informar Ausência
                      </Button>
                    </div>

                    <p className="text-xs text-white text-center">
                      Você pode confirmar múltiplos personagens e indicar pilotos para cada um, se não tiver um piloto específico, escreva "<b>PRECISA DE PILOTO</b>".
                      <br />Só deve usar "<b>INFORMAR AUSÊNCIA</b>" se não puder participar e não emprestar o personagem.
                    </p>
                  </div>
                );
              })()}
            </div>
          </Card>
        </motion.div>
      )}

      {/* Proof submission section for completed events */}
      {event.status === 'COMPLETED' && (() => {
        const userParticipations = participants.filter(
          p => p.userId === user?.id && (['PENDING', 'CONFIRMED', 'PARTICIPATED', 'PROOF_SUBMITTED', 'PROOF_APPROVED', 'PROOF_REJECTED', 'POINTS_AWARDED', 'AWAITING_EVIDENCE', 'CHARACTER_LENT'].includes(p.status))
        );
        
        if (userParticipations.length === 0) return null;

        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
            className="mb-8"
          >
            <Card variant="glass">
              <div className="p-6">
                <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <CheckCircle className="w-6 h-6 text-cyan-400" />
                  Suas Participações
                </h3>
                
                <div className="space-y-3">
                  {userParticipations.map(participation => (
                    <div key={participation.id} className="bg-white/5 rounded-xl p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg flex items-center justify-center text-white font-bold">
                            {participation.character?.name?.[0] || '?'}
                          </div>
                          <div>
                            <p className="font-semibold text-white">
                              {participation.character?.name}
                            </p>
                            <p className="text-xs text-gray-400">
                              {participation.character?.class} • Nv. {participation.character?.level}
                            </p>
                          </div>
                        </div>
                        <div className={`px-3 py-1 rounded-lg text-xs font-medium ${
                          participation.status === 'POINTS_AWARDED' || participation.status === 'PROOF_APPROVED'
                            ? 'bg-green-500/20 text-green-400'
                            : participation.status === 'PROOF_SUBMITTED'
                            ? 'bg-amber-500/20 text-amber-400'
                            : participation.status === 'PROOF_REJECTED'
                            ? 'bg-red-500/20 text-red-400'
                            : 'bg-blue-500/20 text-blue-400'
                        }`}>
                          {getStatusParticipationText(participation.status)}
                        </div>
                      </div>

                      {participation.status === 'AWAITING_EVIDENCE' && (
                        <Button
                          size="small"
                          onClick={() => setProofModalParticipation(participation)}
                          className="w-full bg-green-600 hover:bg-green-700"
                        >
                          <Upload className="w-3 h-3 mr-2" />
                          Enviar Prova
                        </Button>
                      )}

                      {participation.status === 'PROOF_SUBMITTED' && (
                        <div className="text-xs text-amber-300">
                          Prova enviada em {new Date(participation.proofSubmittedAt).toLocaleString('pt-BR')}
                        </div>
                      )}

                      {participation.status === 'PROOF_REJECTED' && (
                        <div className="space-y-2">
                          {participation.rejectionReason && (
                            <p className="text-xs text-red-300">
                              Motivo: {participation.rejectionReason}
                            </p>
                          )}
                          <Button
                            size="small"
                            onClick={() => setProofModalParticipation(participation)}
                            variant="danger"
                            className="w-full"
                          >
                            <Upload className="w-3 h-3 mr-2" />
                            Reenviar Prova
                          </Button>
                        </div>
                      )}

                      {(participation.status === 'POINTS_AWARDED' || participation.status === 'PROOF_APPROVED') && (
                        <div className="text-xs text-green-300">
                          ✅ {participation.pointsAwarded || event.points} pontos creditados
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </Card>
          </motion.div>
        );
      })()}

      {/* Participation Actions */}
      {(canParticipate || userParticipation) && false && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          className="mb-8"
        >
          <Card variant="glass">
            <div className="p-6">
              <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                {userParticipation ? (
                  <>
                    <CheckCircle className="w-6 h-6 text-cyan-400" />
                    Sua Participação
                  </>
                ) : (
                  <>
                    <Users className="w-6 h-6 text-cyan-400" />
                    Participar do Evento
                  </>
                )}
              </h3>

              {userParticipation ? (
                <div className="space-y-4">
                  <div className="bg-white/5 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg flex items-center justify-center text-white font-bold text-lg">
                          {userParticipation.character?.name?.[0] || '?'}
                        </div>
                        <div>
                          <p className="font-semibold text-white text-lg">
                            {userParticipation.character?.name || 'Personagem'}
                          </p>
                          <p className="text-sm text-gray-400">
                            {userParticipation.character?.class || 'Classe'} • Nível {userParticipation.character?.level || 0}
                          </p>
                        </div>
                      </div>
                      <div className={`px-4 py-2 rounded-lg text-sm font-medium border ${
                        userParticipation.status === 'CONFIRMED' ? 'bg-green-500/20 text-green-400 border-green-500/30' :
                        userParticipation.status === 'DECLINED' ? 'bg-red-500/20 text-red-400 border-red-500/30' :
                        'bg-gray-500/20 text-gray-400 border-gray-500/30'
                      }`}>
                        {getStatusParticipationText(userParticipation.status)}
                      </div>
                    </div>
                  </div>

                  {userParticipation.status === 'DECLINED' && userParticipation.declineReason && (
                    <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-4">
                      <p className="text-sm text-orange-400">
                        <strong>Motivo da ausência:</strong> {userParticipation.declineReason}
                      </p>
                    </div>
                  )}

                  {/* Proof submission section - CONFIRMED status */}
                  {event.status === 'COMPLETED' && userParticipation.status === 'CONFIRMED' && !userParticipation.proofSubmittedAt && (
                    <div className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/30 rounded-xl p-4">
                      <div className="flex items-start gap-3 mb-3">
                        <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-sm text-green-200 mb-2">
                            <strong>Evento concluído!</strong> Envie sua prova de participação para receber os pontos.
                          </p>
                          <p className="text-xs text-green-300">
                            Faça upload de uma foto que comprove sua participação. A prova será analisada por um administrador.
                          </p>
                        </div>
                      </div>
                      <Button
                        onClick={() => setProofModalParticipation(userParticipation)}
                        variant="success"
                        className="w-full"
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        📸 Enviar Prova de Participação
                      </Button>
                    </div>
                  )}

                  {/* Proof submission section - AWAITING_EVIDENCE status */}
                  {userParticipation.status === 'AWAITING_EVIDENCE' &&
                   (event.status === 'COMPLETED' || new Date(event.startDate) <= new Date()) &&
                   !userParticipation.proofSubmittedAt && (
                    <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
                      <div className="flex items-start gap-3 mb-3">
                        <AlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-sm text-blue-200 mb-2">
                            <strong>Envie sua prova de presença</strong> para receber os pontos do evento!
                          </p>
                          <p className="text-xs text-blue-300">
                            Faça upload de uma foto que comprove sua participação. A prova será analisada por um administrador.
                          </p>
                        </div>
                      </div>
                      <Button
                        onClick={() => setProofModalParticipation(userParticipation)}
                        className="w-full bg-blue-600 hover:bg-blue-700"
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        Enviar Prova de Presença
                      </Button>
                    </div>
                  )}

                  {/* Proof status indicators */}
                  {userParticipation.proofSubmittedAt && (
                    <div className={`rounded-xl p-4 border ${
                      userParticipation.status === 'PROOF_SUBMITTED' 
                        ? 'bg-amber-500/10 border-amber-500/30' 
                        : userParticipation.status === 'PROOF_APPROVED' || userParticipation.status === 'POINTS_AWARDED'
                        ? 'bg-green-500/10 border-green-500/30'
                        : 'bg-red-500/10 border-red-500/30'
                    }`}>
                      <div className="flex items-start gap-3">
                        {userParticipation.status === 'PROOF_SUBMITTED' && (
                          <Clock className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                        )}
                        {(userParticipation.status === 'PROOF_APPROVED' || userParticipation.status === 'POINTS_AWARDED') && (
                          <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                        )}
                        {userParticipation.status === 'PROOF_REJECTED' && (
                          <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                        )}
                        <div className="flex-1">
                          {userParticipation.status === 'PROOF_SUBMITTED' && (
                            <>
                              <p className="text-sm text-amber-200 font-medium mb-1">
                                Prova enviada - Aguardando análise
                              </p>
                              <p className="text-xs text-amber-300">
                                Enviada em: {new Date(userParticipation.proofSubmittedAt).toLocaleString('pt-BR')}
                              </p>
                            </>
                          )}
                          {(userParticipation.status === 'PROOF_APPROVED' || userParticipation.status === 'POINTS_AWARDED') && (
                            <>
                              <p className="text-sm text-green-200 font-medium mb-1">
                                ✅ Prova aprovada - {userParticipation.pointsAwarded || event.points} pontos creditados!
                              </p>
                              <p className="text-xs text-green-300">
                                Aprovada em: {new Date(userParticipation.adminApprovedAt || userParticipation.pointsAwardedAt).toLocaleString('pt-BR')}
                              </p>
                            </>
                          )}
                          {userParticipation.status === 'PROOF_REJECTED' && (
                            <>
                              <p className="text-sm text-red-200 font-medium mb-1">
                                ❌ Prova rejeitada
                              </p>
                              {userParticipation.rejectionReason && (
                                <p className="text-xs text-red-300 mb-2">
                                  Motivo: {userParticipation.rejectionReason}
                                </p>
                              )}
                              <Button
                                size="small"
                                onClick={() => setProofModalParticipation(userParticipation)}
                                variant="danger"
                                className="mt-2"
                              >
                                <Upload className="w-3 h-3 mr-1" />
                                📸 Reenviar Prova
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {event.status === 'SCHEDULED' && (
                    <div className="grid grid-cols-1">
                      {userParticipation.status !== 'CONFIRMED' && (
                        <select
                          onChange={(e) => {
                            if (e.target.value) {
                              handleParticipate(e.target.value);
                              e.target.value = '';
                            }
                          }}
                          disabled={actionLoading}
                          className="w-full px-4 py-3 bg-green-500/10 border border-green-500/30 text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500/50 transition-all disabled:opacity-50"
                          defaultValue=""
                        >
                          <option value="" className="bg-slate-800">✅ Confirmar com personagem...</option>
                          {characters.map(char => (
                            <option key={char.id} value={char.id} className="bg-slate-800">
                              {char.name} (Nv. {char.level} • {char.class})
                            </option>
                          ))}
                        </select>
                      )}
                      {userParticipation.status !== 'DECLINED' && (
                        <Button
                          variant="secondary"
                          onClick={handleDeclineClick}
                          disabled={actionLoading}
                          fullWidth
                        >
                          <XCircle className="w-4 h-4 mr-2" />
                          Mudar para "Não Vou"
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-gray-400">
                    Escolha um personagem para participar deste evento ou informe que não poderá comparecer.
                  </p>

                  <div className="grid grid-cols-1">
                    <select
                      onChange={(e) => {
                        if (e.target.value) {
                          handleParticipate(e.target.value);
                          e.target.value = '';
                        }
                      }}
                      disabled={actionLoading}
                      className="w-full px-4 py-3 bg-green-500/10 border border-green-500/30 text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500/50 transition-all disabled:opacity-50"
                      defaultValue=""
                    >
                      <option value="" className="bg-slate-800">✅ Participar com personagem...</option>
                      {characters.map(char => (
                        <option key={char.id} value={char.id} className="bg-slate-800">
                          {char.name} (Nv. {char.level} • {char.class})
                        </option>
                      ))}
                    </select>

                    <Button
                      variant="secondary"
                      onClick={handleDeclineClick}
                      disabled={actionLoading}
                      fullWidth
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Não Poderei Comparecer
                    </Button>
                  </div>

                  {characters.length === 0 && (
                    <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
                      <p className="text-sm text-amber-400">
                        ⚠️ Você precisa criar um personagem antes de participar de eventos.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </Card>
        </motion.div>
      )}

      {/* Participants List */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.2 }}
      >
        <Card variant="glass">
          <div className="p-6">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Users className="w-6 h-6 text-cyan-400" />
              Participantes ({participants.length})
            </h3>
            {participants.length > 0 ? (
              <div className="space-y-3">
                {participants.map((participant, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    className="bg-white/5 rounded-xl p-4 hover:bg-white/10 transition-all duration-200"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 flex-1">
                        <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg flex items-center justify-center text-white font-bold">
                          {participant.character?.name?.[0] || participant.user?.name?.[0] || '?'}
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-white">
                            {participant.character?.name || participant.user?.name || 'Usuário'}
                          </p>
                          <p className="text-sm text-gray-400">
                            {participant.user?.name || 'Membro'}
                          </p>
                          {/* Pilot field */}
                          {editingPilot === participant.id ? (
                            <div className="flex items-center gap-2 mt-2">
                              <input
                                type="text"
                                value={pilotValue}
                                onChange={(e) => setPilotValue(e.target.value)}
                                placeholder="Nome do piloto..."
                                className="flex-1 px-2 py-1 bg-slate-700 border border-slate-600 rounded text-sm text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                                autoFocus
                              />
                              <Button
                                size="small"
                                onClick={() => handleSavePilot(participant.id)}
                                className="px-2 py-1"
                              >
                                <CheckCircle className="w-4 h-4" />
                              </Button>
                              <Button
                                size="small"
                                variant="outline"
                                onClick={handleCancelEditPilot}
                                className="px-2 py-1"
                              >
                                <XCircle className="w-4 h-4" />
                              </Button>
                            </div>
                          ) : (user?.role === 'ADMIN' || participant.character?.userId === user?.id) && canPilot && (
                            <div className="flex items-center gap-2 mt-1">
                              <UserCheck className="w-3 h-3 text-gray-500" />
                              <span className="text-xs text-gray-400">
                                Piloto: {participant.pilot || 'Não definido'}
                              </span>
                              <button
                                onClick={() => handleEditPilot(participant)}
                                className="text-cyan-400 hover:text-cyan-300 text-xs underline ml-1"
                              >
                                editar
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className={`px-3 py-1 rounded-lg text-xs font-medium border ${
                          participant.status === 'CONFIRMED' ? 'bg-green-500/20 text-green-400 border-green-500/30' :
                          participant.status === 'CHARACTER_LENT' ? 'bg-amber-500/20 text-amber-400 border-amber-500/30' :
                          participant.status === 'PENDING' ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' :
                          participant.status === 'DECLINED' ? 'bg-red-500/20 text-red-400 border-red-500/30' :
                          'bg-gray-500/20 text-gray-400 border-gray-500/30'
                        }`}>
                          {getStatusParticipationText(participant.status)}
                        </div>
                        
                        {/* Botão admin para confirmar participação sem prova */}
                        {user?.role === 'ADMIN' && participant.status === 'AWAITING_EVIDENCE' && event.status === 'COMPLETED' && (
                          <button
                            onClick={() => handleAdminConfirmParticipation(participant.id)}
                            disabled={actionLoading}
                            className="px-2 py-1 bg-purple-500/20 text-purple-400 border border-purple-500/30 rounded-lg text-xs font-medium hover:bg-purple-500/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                            title="Confirmar participação sem necessidade de prova"
                          >
                            <CheckCircle className="w-3 h-3" />
                            Aprovar
                          </button>
                        )}

                        {user?.role === 'ADMIN' && participant.proofImageUrl && (
                          <button
                            onClick={() => openModal(participant)}
                            disabled={actionLoading}
                            className="px-2 py-1 bg-blue-500/20 text-blue-400 border border-blue-500/30 rounded-lg text-xs font-medium hover:bg-blue-500/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                            title="Visualizar prova"
                          >
                            <Camera className="w-3 h-3" />
                            Visualizar
                          </button>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Users className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400 text-lg mb-2">Nenhum participante ainda</p>
                <p className="text-gray-500 text-sm">Seja o primeiro a se inscrever neste evento!</p>
              </div>
            )}
          </div>
        </Card>
      </motion.div>

      {/* Review Modal */}
      {showModal && selectedProof && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-slate-800/95 to-slate-900/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/10 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <h2 className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                📸 Prova recebida
              </h2>
              <button
                onClick={() => {
                  setShowModal(false);
                  setSelectedProof(null);
                }}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {/* Proof Image */}
              <div>
                <img
                  src={getImageUrl(selectedProof.proofImageUrl)}
                  alt="Prova de presença"
                  className="w-full max-h-[500px] object-contain rounded-xl border border-white/10 bg-slate-800"
                  onError={(e) => {
                    console.error('Erro ao carregar imagem:', selectedProof.proofImageUrl);
                    e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iIzMzMyIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5JbWFnZW0gbsOjbyBlbmNvbnRyYWRhPC90ZXh0Pjwvc3ZnPg==';
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Decline Event Modal */}
      <DeclineEventModal
        isOpen={showDeclineModal}
        onClose={() => setShowDeclineModal(false)}
        onConfirm={handleDeclineConfirm}
        event={event}
        loading={actionLoading}
      />

      {/* Submit Proof Modal — one per participation */}
      <SubmitProofModal
        isOpen={!!proofModalParticipation}
        onClose={() => setProofModalParticipation(null)}
        event={event}
        participation={proofModalParticipation}
        onSuccess={() => {
          setProofModalParticipation(null);
          loadEventDetails();
          notify.success('Prova enviada com sucesso! Aguarde a aprovação.', 'Enviado');
        }}
      />

      {/* Edit Event Modal */}
      <EditEventModal
        event={event}
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSave={handleEditEvent}
      />

      {/* Cancel Event Modal */}
      <CancelEventModal
        event={event}
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onConfirm={handleCancelEvent}
      />

      {/* Party Generator Modal */}
      <PartyGeneratorModal
        isOpen={showPartyModal}
        onClose={() => setShowPartyModal(false)}
        event={event}
        participations={participants}
      />

      {/* Bulk Confirm Participants Modal */}
      <BulkConfirmParticipantsModal
        isOpen={showBulkConfirmModal}
        onClose={() => setShowBulkConfirmModal(false)}
        event={event}
        existingParticipants={participants}
        onConfirm={handleBulkConfirm}
      />

      {/* Multi Character Participation Modal */}
      <MultiCharacterParticipationModal
        isOpen={showMultiCharModal}
        onClose={() => setShowMultiCharModal(false)}
        characters={characters}
        currentParticipations={participants.filter(p => p.userId === user?.id)}
        eventType={event?.type}
        onConfirm={handleMultiCharParticipation}
      />

      {/* Lend Character Modal */}
      <LendCharacterModal
        isOpen={showLendCharModal}
        onClose={() => setShowLendCharModal(false)}
        characters={characters}
        currentParticipations={participants.filter(p => p.userId === user?.id)}
        onConfirm={handleLendCharacter}
        loading={actionLoading}
      />

      {/* Non Participants Modal */}
      <NonParticipantsModal
        isOpen={showNonParticipantsModal}
        onClose={() => setShowNonParticipantsModal(false)}
        nonParticipants={nonParticipants}
        loading={loadingNonParticipants}
      />
    </PageContainer>
  );
};

export default EventDetail;
